import { useEffect, useState } from "react";
import { computeParts, formatClock } from "@/lib/timer";
import { mediaUrl } from "@/lib/api";
import DateTimeBadge from "./DateTimeBadge";

function useTick() {
  const [, setT] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setT((x) => x + 1), 200);
    return () => clearInterval(i);
  }, []);
}

function CardBadge({ color, count, testid }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="w-7 h-10 rounded-[3px]" style={{ background: color }} />
      <span className="font-mono-num font-bold text-4xl">{count}</span>
    </div>
  );
}

function MediaBanner({ media, slot, height }) {
  const [idx, setIdx] = useState(0);
  const active = media.filter((m) => m.active && (m.slot || "fullscreen") === slot);
  const activeKey = active.map((m) => `${m.id}:${m.duration}`).join(",");
  useEffect(() => {
    if (active.length <= 1) return;
    const cur = active[idx % active.length];
    const dur = (cur?.duration || 10) * 1000;
    const t = setTimeout(() => setIdx((i) => i + 1), dur);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [idx, activeKey]);

  if (active.length === 0) {
    return (
      <div className="w-full flex items-center justify-center bg-[#0A0C10] border border-[#1E2638]" style={{ height }}>
        <span className="font-display uppercase tracking-[0.3em] text-[#3a465e] text-2xl">Sponsoren</span>
      </div>
    );
  }
  const cur = active[idx % active.length];
  return (
    <div className="w-full overflow-hidden bg-black flex items-center justify-center" style={{ height }}>
      {cur.type === "video" ? (
        <video src={mediaUrl(cur.id)} autoPlay muted loop className="w-full h-full object-contain" />
      ) : cur.type === "pdf" ? (
        <iframe src={`${mediaUrl(cur.id)}#toolbar=0&navpanes=0`} title="pdf" className="w-full h-full bg-white" />
      ) : (
        <img src={mediaUrl(cur.id)} alt="" className="w-full h-full object-contain" />
      )}
    </div>
  );
}

export default function DisplayScoreboard({ snapshot, live: liveProp }) {
  useTick();
  const { settings, media, matches } = snapshot;
  const live = liveProp || (snapshot.lives && snapshot.lives[0]) || snapshot.live;
  const currentMatch = (matches || []).find((m) => m.id === live.match_id);
  const field = live.field_name || currentMatch?.field;
  const fieldColor = live.field_color || "#00F0FF";
  const parts = computeParts(live.timer);
  const clock = formatClock(parts.main);
  const phase = live.timer?.phase || "playing";
  const half = live.timer?.half || 1;
  const ended = live.status === "ended" || phase === "ended";
  const showTimer = settings.show_timer !== false;
  const large = settings.display_mode === "large";

  return (
    <ScoreboardFrame snapshot={snapshot}>
      <TeamPanel name={live.team1_name} logo={live.team1_logo} color="#0088FF" cards={live.cards.team1} align="left" testid="t1" large={large} teamColor={live.team1_color} teamShort={live.team1_short} />

      <div className={`${large ? "w-[860px]" : "w-[760px]"} flex flex-col items-center justify-center bg-[#050609] border-x-2 border-[#1E2638]`}>
        <div className="flex items-center gap-10">
          <span className={`font-mono-num font-extrabold ${large ? "text-[260px]" : "text-[210px]"} leading-none text-[#0088FF]`} data-testid="display-score1">
            {live.score1}
          </span>
          <span className={`font-mono-num font-extrabold ${large ? "text-[150px]" : "text-[110px]"} leading-none text-[#2b3550]`}>:</span>
          <span className={`font-mono-num font-extrabold ${large ? "text-[260px]" : "text-[210px]"} leading-none text-[#FF6600]`} data-testid="display-score2">
            {live.score2}
          </span>
        </div>
        <div className="mt-4 flex flex-col items-center">
          {ended ? (
            <div className={`font-display font-black uppercase tracking-[0.2em] ${large ? "text-8xl" : "text-6xl"} text-[#E5FF00]`} data-testid="display-ended">
              Spielende
            </div>
          ) : phase === "halftime" ? (
            <div className={`font-display font-black uppercase tracking-[0.2em] ${large ? "text-8xl" : "text-6xl"} text-[#00F0FF]`} data-testid="display-halftime">
              Halbzeit
            </div>
          ) : !showTimer ? (
            <div className={`font-display font-black uppercase tracking-[0.2em] ${large ? "text-8xl" : "text-6xl"} text-[#8A99AD]`} data-testid="display-phase">{half}. Halbzeit</div>
          ) : (
            <>
              <div className={`font-display uppercase tracking-widest ${large ? "text-5xl" : "text-3xl"} text-[#8A99AD]`} data-testid="display-half">{half}. Halbzeit</div>
              <div className={`font-mono-num font-bold ${large ? "text-9xl" : "text-8xl"} text-[#E5FF00]`} data-testid="display-clock">{clock}</div>
              {parts.injury > 0 && (
                <div className={`font-display uppercase tracking-widest ${large ? "text-5xl" : "text-3xl"} text-[#00F0FF] mt-1`} data-testid="display-injury">+{formatClock(parts.injury)}</div>
              )}
            </>
          )}
        </div>
        {field && (
          <div className={`mt-6 font-display font-black uppercase tracking-wider ${large ? "text-4xl" : "text-3xl"} rounded-xl px-6 py-2`} style={{ background: fieldColor, color: "#0A0C10" }} data-testid="display-scoreboard-field">{field}</div>
        )}
      </div>

      <TeamPanel name={live.team2_name} logo={live.team2_logo} color="#FF6600" cards={live.cards.team2} align="right" testid="t2" large={large} teamColor={live.team2_color} teamShort={live.team2_short} />
    </ScoreboardFrame>
  );
}

export function ScoreboardFrame({ snapshot, children }) {
  const { settings, media } = snapshot;
  const large = settings.display_mode === "large";
  return (
    <div className="w-full h-full flex flex-col text-white">
      {/* TOP: title + logo sponsor */}
      <div className={`flex items-stretch ${large ? "h-[240px]" : "h-[190px]"} border-b-4 border-[#E5FF00]`}>
        <div className="flex-1 flex items-center justify-between px-14">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-4 h-4 rounded-full bg-[#FF2A55] live-dot" />
              <span className="font-display uppercase tracking-[0.35em] text-[#FF2A55] text-2xl">Live</span>
            </div>
            <h1 className={`font-display font-black uppercase tracking-wide ${large ? "text-8xl" : "text-6xl"} leading-none`} data-testid="display-event-title">
              {settings.event_title}
            </h1>
          </div>
          <DateTimeBadge show={settings.show_datetime} />
        </div>
        <div className="w-[560px] p-4">
          <MediaBanner media={media} slot="logo" height={large ? 200 : 158} />
        </div>
      </div>

      {/* CENTER */}
      <div className="flex-1 flex items-stretch overflow-hidden">{children}</div>

      {/* BOTTOM sponsor banner */}
      <div className={`${large ? "h-[200px]" : "h-[150px]"} border-t-4 border-[#E5FF00] p-4`}>
        <MediaBanner media={media} slot="banner" height={large ? 168 : 118} />
      </div>
    </div>
  );
}

function TeamPanel({ name, logo, color, cards, align, testid, large, teamColor, teamShort }) {
  const badge = (teamShort && teamShort.trim()) || name?.[0] || "?";
  return (
    <div className={`flex-1 flex flex-col justify-center px-12 ${align === "right" ? "items-end text-right" : "items-start"}`}>
      <div className={`flex items-center gap-6 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <div className={`${large ? "w-40 h-40" : "w-28 h-28"} rounded-2xl flex items-center justify-center overflow-hidden`} style={{ background: teamColor || color }}>
          {logo ? <img src={logo} alt="" className="w-full h-full object-cover" /> : <span className={`font-display font-black text-white/90 ${badge.length > 2 ? "text-4xl" : badge.length > 1 ? "text-5xl" : "text-6xl"}`}>{badge}</span>}
        </div>
      </div>
      <h2 className={`font-display font-black uppercase tracking-wide ${large ? "text-8xl max-w-[540px]" : "text-6xl max-w-[440px]"} mt-6 leading-none`} data-testid={`display-${testid}-name`}>
        {name}
      </h2>
      <div className={`flex gap-6 mt-6 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <CardBadge color="#FFD700" count={cards.yellow} testid={`display-${testid}-yellow`} />
        <CardBadge color="#FF2A55" count={cards.red} testid={`display-${testid}-red`} />
      </div>
    </div>
  );
}
